import { Injectable } from '@angular/core';
import * as xmljs from 'xml-js';

interface XmlNode {
  type?: string;
  name?: string;
  elements?: XmlNode[];
  text?: any;
  attributes?: Record<string, any>;
}

interface ChildInfo {
  name: string;
  min: number;
  max: number;
  paths: number;
}

interface Def {
  name: string;
  count: number;
  children: Map<string, ChildInfo>;
  attrs: Map<string, { count: number; required: boolean }>;
  orders: string[][];
  hasText: boolean;
  mixed: boolean;
}

@Injectable({ providedIn: 'root' })
export class XmlToXsdConverterService {

  // Validation

  validateXml(xml: string): boolean {
    try {
      xmljs.xml2js(xml, { compact: false });
      return true;
    } catch {
      return false;
    }
  }

  convertXmlToXsd(xml: string): string {
    const json: any = xmljs.xml2js(xml, {
      compact: false,
      alwaysChildren: true,
      elementsKey: 'elements'
    });

    const map = this.analyze(json);
    const root = Array.from(map.values())[0];

    const out: string[] = [];
    out.push('<?xml version="1.0" encoding="UTF-8"?>');
    out.push('<xs:schema xmlns:xs="http://www.w3.org/2001/XMLSchema" elementFormDefault="qualified" attributeFormDefault="unqualified">');

    this.write(root, map, out, 1, []);

    out.push('</xs:schema>');
    return out.join('\n');
  }

  // Analysis
  private analyze(root: any): Map<string, Def> {
    const map = new Map<string, Def>();
    const rootEl = root.elements?.find((e: any) => e.type === 'element');
    if (rootEl) this.walk(rootEl, map, []);
    return map;
  }

  private walk(node: XmlNode, map: Map<string, Def>, path: string[]) {
    const key = [...path, node.name!].join('/');

    if (!map.has(key)) {
      map.set(key, {
        name: node.name!,
        count: 0,
        children: new Map(),
        attrs: new Map(),
        orders: [],
        hasText: false,
        mixed: false
      });
    }

    const def = map.get(key)!;
    def.count++;

    const order: string[] = [];
    const occ = new Map<string, number>();

    node.elements?.forEach(child => {
      if (child.type === 'element') {
        order.push(child.name!);
        occ.set(child.name!, (occ.get(child.name!) || 0) + 1);

        const childKey = [...path, node.name!, child.name!].join('/');

        if (!def.children.has(child.name!)) {
          def.children.set(child.name!, {
            name: child.name!,
            min: 1,
            max: 1,
            paths: 0
          });
        }

        const c = def.children.get(child.name!)!;
        c.paths++;

        this.walk(child, map, [...path, node.name!]);
      }

      const text = (child.text ?? '').toString().trim();
      if (text) def.hasText = true;
    });

    def.orders.push(order);

    def.children.forEach((c, name) => {
      const count = occ.get(name) || 0;
      if (count === 0) c.min = 0;
      if (count > c.max) c.max = count;
    });

    Object.entries(node.attributes || {}).forEach(([k]) => {
      if (!def.attrs.has(k)) {
        def.attrs.set(k, { count: 0, required: true });
      }
      const a = def.attrs.get(k)!;
      a.count++;
      if (a.count < def.count) a.required = false;
    });

    if (def.hasText && def.children.size > 0) {
      def.mixed = true;
    }
  }

  // Generation

  private write(def: Def, map: Map<string, Def>, out: string[], indent: number, path: string[], occ?: ChildInfo) {
    const ind = '  '.repeat(indent);

    const min = occ && occ.min === 0 ? ' minOccurs="0"' : '';
    const max = occ && occ.max > 1 ? ' maxOccurs="unbounded"' : '';

    const hasChildren = def.children.size > 0;
    const hasAttrs = def.attrs.size > 0;

    if (!hasChildren && !hasAttrs) {
      out.push(`${ind}<xs:element name="${def.name}" type="xs:string"${min}${max}/>`);
      return;
    }

    // Simple Content
    if (!hasChildren && hasAttrs) {
      out.push(`${ind}<xs:element name="${def.name}"${min}${max}>`);
      out.push(`${ind}  <xs:complexType>`);
      out.push(`${ind}    <xs:simpleContent>`);
      out.push(`${ind}      <xs:extension base="xs:string">`);

      def.attrs.forEach((a, name) => {
        const use = a.required ? 'required' : 'optional';
        out.push(`${ind}        <xs:attribute name="${name}" type="xs:string" use="${use}"/>`);
      });

      out.push(`${ind}      </xs:extension>`);
      out.push(`${ind}    </xs:simpleContent>`);
      out.push(`${ind}  </xs:complexType>`);
      out.push(`${ind}</xs:element>`);
      return;
    }

    // Complex
    out.push(`${ind}<xs:element name="${def.name}"${min}${max}>`);
    out.push(`${ind}  <xs:complexType${def.mixed ? ' mixed="true"' : ''}>`);

    const unstable = this.isOrderUnstable(def);
    const useChoice = def.mixed || unstable;

    if (hasChildren) {
      out.push(`${ind}    <xs:${useChoice ? 'choice' : 'sequence'} minOccurs="0" maxOccurs="unbounded">`);

      def.children.forEach(c => {
        const childKey = [...path, def.name, c.name].join('/');
        const childDef = map.get(childKey);

        if (childDef) {
          this.write(childDef, map, out, indent + 3, [...path, def.name], c);
        }
      });

      out.push(`${ind}    </xs:${useChoice ? 'choice' : 'sequence'}>`);
    }

    def.attrs.forEach((a, name) => {
      const use = a.required ? 'required' : 'optional';
      out.push(`${ind}    <xs:attribute name="${name}" type="xs:string" use="${use}"/>`);
    });

    out.push(`${ind}  </xs:complexType>`);
    out.push(`${ind}</xs:element>`);
  }

  // Order Analysis

  private isOrderUnstable(def: Def): boolean {
    if (def.orders.length < 2) return false;

    const normalized = def.orders.map(o => o.join(','));
    return new Set(normalized).size > 1;
  }
}
